import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Flame, CheckCircle2, TrendingUp, StickyNote, Highlighter,
  Sparkles, Brain, Download, BarChart2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CAT_NAMES = { A: "Fundamentals", B: "Tech & Digital", C: "AI & Ethics" };

export default function Stats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/stats`)
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const exportMarkdown = async () => {
    setExporting(true);
    try {
      const notesRes = await axios.get(`${API}/notes`);
      const notes = notesRes.data;
      if (!notes.length) { alert("Belum ada catatan untuk diekspor."); return; }

      const lessonTitles = {};
      (stats?.completed_lessons || []).forEach(l => { lessonTitles[l.lesson_id] = l.lesson_title; });

      const byLesson = {};
      notes.forEach(n => {
        if (!byLesson[n.lesson_id]) byLesson[n.lesson_id] = { notes: [], highlights: [] };
        if (n.note_type === "highlight") byLesson[n.lesson_id].highlights.push(n);
        else byLesson[n.lesson_id].notes.push(n);
      });

      let md = "# BehaviorBase — Catatan Belajar\n";
      md += "> Diekspor pada " + new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) + "\n";
      md += "> Total: " + notes.filter(n => n.note_type !== "highlight").length + " catatan, " + notes.filter(n => n.note_type === "highlight").length + " highlight\n\n---\n\n";

      Object.entries(byLesson).forEach(([lessonId, data]) => {
        const title = lessonTitles[lessonId] || "Lesson";
        md += "## " + title + "\n\n";
        if (data.highlights.length > 0) {
          md += "### Highlights\n\n";
          data.highlights.forEach(h => { md += "> " + h.content + "\n\n"; });
        }
        if (data.notes.length > 0) {
          md += "### Catatan\n\n";
          data.notes.forEach(n => {
            const d = new Date(n.created_at).toLocaleDateString("id-ID");
            md += "**" + d + "**\n" + n.content + "\n\n";
          });
        }
        md += "---\n\n";
      });

      const blob = new Blob([md], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "behaviorbase-catatan-" + new Date().toISOString().slice(0, 10) + ".md";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportDone(true);
      setTimeout(() => setExportDone(false), 3000);
    } catch (err) {
      console.error(err);
      alert("Gagal export catatan.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <PageLoader />;

  const categories = stats?.categories || {};
  const completedLessons = stats?.completed_lessons || [];
  const vaultByStatus = stats?.vault_by_status || {};
  const totalCompleted = completedLessons.length;
  const pctOverall = Math.round((totalCompleted / 20) * 100);

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <p className="text-xs text-indigo-400 font-mono mb-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>// stats &middot; belajarmu</p>
        <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Stats &amp; Analytics
        </h1>
      </motion.div>

      {/* Overall Progress Banner */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="mb-6 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-indigo-400 font-mono" style={{ fontFamily: "'JetBrains Mono', monospace" }}>overall progress</p>
            <p className="text-2xl font-bold text-white mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {totalCompleted} <span className="text-[#94A3B8] text-lg font-normal">/ 20 lesson</span>
            </p>
          </div>
          <div className="text-3xl font-bold text-indigo-400" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{pctOverall}%</div>
        </div>
        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${pctOverall}%` }} transition={{ duration: 1, delay: 0.3 }}
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" />
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard icon={<Flame className="w-4 h-4" />} label="Streak" value={stats?.streak || 0} sub={`terpanjang ${stats?.longest_streak || 0} hari`} accent="orange" testId="stat-streak" />
        <StatCard icon={<StickyNote className="w-4 h-4" />} label="Catatan" value={stats?.total_notes || 0} sub={`${stats?.total_highlights || 0} highlight`} accent="indigo" testId="stat-notes" />
        <StatCard icon={<Sparkles className="w-4 h-4" />} label="Ide Konten" value={stats?.total_ideas || 0} sub="di content vault" accent="purple" testId="stat-ideas" />
        <StatCard icon={<Brain className="w-4 h-4" />} label="Quiz" value={stats?.total_quizzes || 0} sub={stats?.total_quizzes > 0 ? `avg ${stats?.avg_quiz_score}%` : "belum ada"} accent="violet" testId="stat-quiz" />
      </motion.div>

      {/* Category Progress */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
        <h2 className="text-xs text-[#94A3B8] uppercase tracking-widest mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Progress per Kategori</h2>
        <div className="space-y-3">
          {Object.entries(categories).map(([cat, data]) => {
            const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;
            return (
              <div key={cat} className="bg-[#121216] border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded-full border border-indigo-500/20">Kat. {cat}</span>
                    <span className="text-sm font-medium text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{CAT_NAMES[cat]}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#94A3B8]">{data.completed}/{data.total}</span>
                    <span className="text-xs font-mono text-indigo-400">{pct}%</span>
                  </div>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, delay: 0.4 }}
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" />
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Content Vault Stats */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="mb-6">
        <h2 className="text-xs text-[#94A3B8] uppercase tracking-widest mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Content Vault</h2>
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: "belum_dibuat", label: "Belum Dibuat", color: "text-[#94A3B8]", border: "border-white/10" },
            { key: "sedang_dikerjakan", label: "Sedang Dikerjakan", color: "text-yellow-400", border: "border-yellow-500/20" },
            { key: "sudah_publish", label: "Sudah Publish", color: "text-emerald-400", border: "border-emerald-500/20" },
          ].map(({ key, label, color, border }) => (
            <div key={key} className={`bg-[#121216] border ${border} rounded-xl p-4`}>
              <div className={`text-2xl font-bold ${color}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{vaultByStatus[key] || 0}</div>
              <div className="text-xs text-[#94A3B8] mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>{label}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Completed Lessons */}
      {completedLessons.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-6">
          <h2 className="text-xs text-[#94A3B8] uppercase tracking-widest mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Lesson Selesai</h2>
          <div className="space-y-2">
            {completedLessons.map((l, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                data-testid={`completed-lesson-${i}`}
                className="flex items-center gap-3 bg-[#121216] border border-white/10 rounded-xl px-4 py-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <p className="flex-1 text-sm text-white font-medium truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{l.lesson_title}</p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] font-mono text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-1.5 py-0.5 rounded-full">Kat. {l.category}</span>
                  {l.completed_at && <span className="text-[10px] text-[#4a5568]">{new Date(l.completed_at).toLocaleDateString("id-ID")}</span>}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Export Section */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
        className="bg-[#121216] border border-white/10 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-white mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Export Semua Catatan</h3>
            <p className="text-xs text-[#94A3B8]" style={{ fontFamily: "'Inter', sans-serif" }}>
              Download semua catatan dan highlight dalam format Markdown (.md)
            </p>
            <div className="flex items-center gap-4 mt-3">
              <span className="text-xs text-[#94A3B8]"><span className="text-white font-medium">{stats?.total_notes || 0}</span> catatan</span>
              <span className="text-xs text-amber-400"><span className="font-medium">{stats?.total_highlights || 0}</span> highlight</span>
              <span className="text-xs text-indigo-400"><span className="font-medium">{completedLessons.length}</span> lesson</span>
            </div>
          </div>
          <button
            data-testid="export-notes-btn"
            onClick={exportMarkdown}
            disabled={exporting}
            className={"flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl transition-all duration-200 flex-shrink-0 " + (
              exportDone ? "bg-emerald-500/20 border border-emerald-500/25 text-emerald-400"
                        : "bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50"
            )}
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {exporting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
             : exportDone ? <CheckCircle2 className="w-4 h-4" />
             : <Download className="w-4 h-4" />}
            {exportDone ? "Berhasil!" : "Export .md"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, accent, testId }) {
  const styles = {
    orange: "border-orange-500/20 bg-orange-500/5 [&>div:first-child]:text-orange-400",
    indigo: "border-indigo-500/20 bg-indigo-500/5 [&>div:first-child]:text-indigo-400",
    purple: "border-purple-500/20 bg-purple-500/5 [&>div:first-child]:text-purple-400",
    violet: "border-violet-500/20 bg-violet-500/5 [&>div:first-child]:text-violet-400",
  };
  return (
    <div data-testid={testId} className={`border rounded-xl p-4 ${styles[accent] || styles.indigo}`}>
      <div className="mb-2">{icon}</div>
      <div className="text-xl font-bold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value}</div>
      <div className="text-xs text-[#94A3B8] mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>{label}</div>
      {sub && <div className="text-[10px] text-[#4a5568] mt-0.5 font-mono" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{sub}</div>}
    </div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-[60vh]">
      <div className="w-6 h-6 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
    </div>
  );
}
